import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { FileText, Box, FileCheck, Calendar, CheckCircle, ClipboardCheck } from 'lucide-react';

const CustomServices: React.FC = () => {
  const navigate = useNavigate();
  const services = [
    {
      title: 'Design Considerations',
      description: 'Our expert team carefully analyzes project requirements and prepares comprehensive design strategies. We provide detailed technical drawings and specifications tailored to your specific waterproofing needs.',
      icon: <FileText size={24} className="text-[#d40715]" />,
    },
    {
      title: 'Product Selection',
      description: 'We conduct thorough evaluations of waterproofing materials and systems, considering factors like durability, performance, and cost-effectiveness to ensure the best solution for your project.',
      icon: <Box size={24} className="text-[#d40715]" />,
    },
    {
      title: 'Method Statements',
      description: 'Our detailed method statements outline step-by-step procedures, safety measures, and quality control protocols to ensure proper execution of waterproofing works.',
      icon: <FileCheck size={24} className="text-[#d40715]" />,
    },
    {
      title: 'Program Of Works',
      description: 'We develop comprehensive work schedules that optimize project timelines while maintaining quality standards. Our programs account for all project phases and dependencies.',
      icon: <Calendar size={24} className="text-[#d40715]" />,
    },
    {
      title: 'ON-SITE QA',
      description: 'Our rigorous quality assurance process includes regular site inspections, progress monitoring, and detailed documentation to ensure all work meets our high standards.',
      icon: <CheckCircle size={24} className="text-[#d40715]" />,
    },
    {
      title: 'Testing Procedure',
      description: 'We implement comprehensive testing protocols including water testing, adhesion tests, and membrane thickness verification to ensure the effectiveness of all waterproofing applications.',
      icon: <ClipboardCheck size={24} className="text-[#d40715]" />,
    },
  ];

  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    const timelineItems = timelineRef.current?.querySelectorAll('.timeline-item');
    if (timelineItems) {
      timelineItems.forEach((item) => observer.observe(item));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <section className="pt-24 pb-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Customer Service</h2>
            <div className="w-24 h-1 bg-[#d40715] mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-gray-600">
              Our comprehensive approach ensures exceptional quality and efficiency throughout your project lifecycle
            </p>
          </div>

          <div className="max-w-4xl mx-auto relative" ref={timelineRef}>
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 h-full w-0.5 bg-gray-200"></div>

            {services.map((service, index) => (
              <div 
                key={index}
                className="timeline-item relative flex items-start mb-16 md:mb-24"
              >
                <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 z-10">
                  <div className="w-12 h-12 bg-white rounded-full border-2 border-[#d40715] flex items-center justify-center shadow-lg">
                    {service.icon}
                  </div>
                </div>

                <div 
                  className={`ml-16 md:ml-0 md:w-1/2 ${
                    index % 2 === 0 
                      ? 'md:pr-12 md:text-right' 
                      : 'md:pl-12 md:ml-auto'
                  }`}
                >
                  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto mt-16 text-center">
            <p className="text-gray-600 text-lg mb-8">
              Hop Shing Engineering & Construction Limited is proud of its extensive experience and proven track record in delivering high-quality solutions. Please feel free to contact us directly to learn more about our capabilities and discuss how we can support your project needs.
            </p>
            <button 
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="bg-[#d40715] text-white px-8 py-3 rounded-lg text-lg font-medium transition-all duration-300 hover:bg-[#ff2910] transform hover:scale-105"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
      <ScrollToTop />
      <Footer />
    </div>
  );
};

export default CustomServices;